/**
 * Will a SCHEDULED campaign quietly under-deliver for want of credits?
 *
 * WHY THIS EXISTS (2026-08-07). A campaign sent NOW goes through the server's consent flow: it
 * sizes the audience, and if the wallet is short it answers 409 so the person who pressed Send can
 * top up or approve. A campaign sent LATER never touches that flow — `triggerScheduledCampaigns`
 * fires unattended and draws per message. On a site with auto-reload ON that is fine: the draw
 * arms a top-up and the campaign completes. On a site with auto-reload OFF the draw eventually
 * returns insufficient, sends stop, and the campaign HALF-DELIVERS — some patients get it, some
 * never do, and nobody is told.
 *
 * It fails safe on money (no surprise charge), so the harm is the silence, not the spend. The fix
 * is to say something while a human is still present: at schedule time.
 *
 * This is ADVISORY ONLY. Enforcement stays server-side at the send boundary, where the wallet draw
 * lives. A warning computed from client state must never be the thing standing between a practice
 * and its patients — it exists to inform a decision, not to block one.
 *
 * Deliberately NOT warned about:
 *   - `now`   — the server's 409/consent flow already covers it, with the person right there.
 *   - `draft` — nothing is scheduled to send.
 *   - auto-reload ON — the practice opted in to being topped up; that is consent, not a problem.
 *   - unknown state — see UNKNOWN below.
 *
 * UNKNOWN is a first-class outcome, not a silent false. If the balance or the auto-reload config
 * has not loaded, we genuinely cannot say whether this campaign will under-deliver. Returning a
 * confident "you're short" would be a money claim made on missing data, and a warning that cries
 * wolf gets trained away — at which point the real one is invisible too. So `unknown` reports
 * itself honestly and leaves `warn` false, and the caller decides whether to render anything.
 */

// === OUTLOOK-BODY-START === (extracted and evaluated verbatim by the node test — keep pure:
// no imports, no Vue, no DOM, no clock. Anything impure here breaks the test's ability to run it.)
function toCount(n) {
  const v = Number(n);
  return Number.isFinite(v) && v > 0 ? Math.floor(v) : 0;
}

/**
 * @param {{
 *   scheduleType: 'now'|'scheduled'|'draft',
 *   channels: Array<{
 *     product: 'sms'|'email',
 *     creditsNeeded: number,
 *     creditsAvailable: number,
 *     autoReloadEnabled: boolean|null|undefined,
 *     balanceLoaded: boolean
 *   }>
 * }} input
 * @returns {{ warn: boolean, status: 'not-applicable'|'ok'|'covered-by-autoreload'|'unknown'|'shortfall',
 *   shortfalls: Array<{product: string, creditsNeeded: number, creditsAvailable: number, shortfall: number}>,
 *   message: string|null }}
 */
function scheduleCreditOutlook(input) {
  const scheduleType = input && input.scheduleType;
  const channels = (input && Array.isArray(input.channels) ? input.channels : []);

  const none = (status) => ({ warn: false, status, shortfalls: [], message: null });

  // Only a LATER send can under-deliver unattended.
  if (scheduleType !== 'scheduled') return none('not-applicable');

  const shortfalls = [];
  let sawUnknown = false;
  let sawCovered = false;
  let sawActive = false;

  for (const raw of channels) {
    const ch = raw || {};
    const needed = toCount(ch.creditsNeeded);
    if (needed <= 0) continue; // channel not in play for this campaign
    sawActive = true;

    // Cannot assert anything without both halves of the comparison.
    if (!ch.balanceLoaded || ch.autoReloadEnabled === null || ch.autoReloadEnabled === undefined) {
      sawUnknown = true;
      continue;
    }

    const available = toCount(ch.creditsAvailable);
    const shortfall = Math.max(0, needed - available);
    if (shortfall <= 0) continue;

    // A shortfall on a site that opted INTO auto-reload is not a problem — the draw arms a
    // top-up and the campaign completes. Reporting it would train people to ignore this banner.
    if (ch.autoReloadEnabled === true) { sawCovered = true; continue; }

    shortfalls.push({
      product: String(ch.product || 'sms'),
      creditsNeeded: needed,
      creditsAvailable: available,
      shortfall,
    });
  }

  if (shortfalls.length) {
    const parts = shortfalls.map((s) =>
      `${s.product === 'email' ? 'Email' : 'SMS'}: needs ~${s.creditsNeeded.toLocaleString()} credits, ` +
      `${s.creditsAvailable.toLocaleString()} available`
    );
    return {
      warn: true,
      status: 'shortfall',
      shortfalls,
      // Says what happens, then what to do about it. "May not reach everyone" is the honest
      // phrasing: sends stop at the credit floor, they do not fail loudly.
      message:
        `This campaign may not reach everyone. ${parts.join('; ')}. ` +
        `Auto-reload is off, so sending will stop when credits run out. ` +
        `Top up before the send date, or turn on auto-reload.`,
    };
  }
  if (sawUnknown) return none('unknown');
  if (sawCovered) return none('covered-by-autoreload');
  return none(sawActive ? 'ok' : 'not-applicable');
}
// === OUTLOOK-BODY-END ===

export { scheduleCreditOutlook };
